import axios from 'axios';
import { createAsyncThunk, createSlice, PayloadAction, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import {
  ISalaire,
  ISalaireGroup,
  ISalaireGroupDTO,
  defaultSalaireValue,
  defaultSalaireGroupValue,
  ISalaireDashboard,
  SalaireDetails,
  ISalaireRelanceDTO,
} from 'app/shared/model/salaire.model';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from '../../shared/util/entity-utils';

interface State {
  errorMessage: string | null;
  // other properties...
}

const initialState = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<SalaireDetails> | ReadonlyArray<ISalaire>,
  dashBoardEntities: [] as ReadonlyArray<SalaireDetails>,
  salaireGroupEntities: [] as ReadonlyArray<ISalaireGroup>,
  entity: defaultSalaireValue,
  salaireGroup: defaultSalaireGroupValue,
  updating: false,
  updateSuccess: false,
  totalItems: 0,
  selectedSalaires: [] as string[],
  totalAmount: 0,
};

const apiUrl = 'api/salaire';

const getSalaireGroup = async (salaire: ISalaire) => {
  const salaireGroupResponse = await axios.get<ISalaireGroup>(`${apiUrl}/group/${salaire.salaireGroupId}`);
  const salaireGroupData = salaireGroupResponse.data;

  return {
    ...salaire,
    salaireGroup: salaireGroupData,
  };
};

// Actions
export const getSalaireGroupsByEcoleId = createAsyncThunk(
  'salaire/fetch_salaire_groups_by_ecole_id',
  async ({ ecoleId, anneeScolaireId }: ISalaireGroupDTO) => {
    const requestUrl = `${apiUrl}/group/ecole/${ecoleId}?anneeScolaireId=${anneeScolaireId}`;
    return axios.get<ISalaireGroup[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const updateSalaireGroupStatus = createAsyncThunk(
  'salaire/update_salaire_group_status',
  async ({ ecoleId, anneeScolaireId }: { ecoleId: string; anneeScolaireId: string }, thunkAPI) => {
    try {
      const requestUrl = `api/salaire/group/${ecoleId}/status`;
      await axios.get<void>(requestUrl, {
        params: {
          ecoleId,
          anneeScolaireId,
        },
      });
      thunkAPI.dispatch(getSalaireGroupsByEcoleId({ ecoleId, anneeScolaireId }));
      return ecoleId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
  { serializeError: serializeAxiosError }
);

export const getSalairesByProfesseurId = createAsyncThunk('salaire/fetch_salaire_by_prof_id', async (professeurId: string) => {
  const requestUrl = `api/salaires/professeur/${professeurId}`;
  const response = await axios.get<ISalaire[]>(requestUrl);
  const salaireWithDetails = await Promise.all(response.data.map(getSalaireGroup));
  return { salaireWithDetails };
});

export const getSalaireGroupById = createAsyncThunk(
  'salaire/fetch_salaire_group',
  async (salaireGroupId: string) => {
    const requestUrl = `${apiUrl}/group/${salaireGroupId}`;
    return axios.get<ISalaireGroup>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getSalaireDashboard = createAsyncThunk(
  'salaire/fetch_salaire_dashboard',
  async (salaireGroupId: string) => {
    const requestUrl = `${apiUrl}/dashboard/${salaireGroupId}`;
    const result = axios.get<ISalaireDashboard>(requestUrl);
    return (await result).data;
  },
  { serializeError: serializeAxiosError }
);

export const fetchSalaires = createAsyncThunk(
  'salaires/fetch_salaires_list',
  async (entity: ISalaire) => {
    let requestUrl = '';
    let statutSalaire;
    let searchUrl = '';
    if (entity.statutSalaire) {
      statutSalaire = '&statutSalaire=' + entity.statutSalaire;
      searchUrl += statutSalaire;
    }

    if (!entity.statutSalaire && !entity.statutSalaire) {
      requestUrl = `${apiUrl}/group/${entity.salaireGroupId}/list?page=${entity.page}&size=${entity.size}`;
    } else {
      requestUrl = `${apiUrl}/group/${entity.salaireGroupId}/list?page=${entity.page}&size=${entity.size}${
        searchUrl ? '&' : '?'
      }${searchUrl}`;
    }
    const response = await axios.get<SalaireDetails[]>(requestUrl);
    const totalItems = response.headers['x-total-count'];

    return { salaireWithDetails: response.data, totalItems };
  },
  { serializeError: serializeAxiosError }
);

export const paySalairesToProfesseurs = createAsyncThunk(
  'salaire/pay_salaires_to_professeurs',
  async (entity: ISalaireGroup & { selectedSalaires?: string[] }, thunkAPI) => {
    const requestUrl = `${apiUrl}/payer/${entity.id}`;
    const response = await axios.post<ISalaire[]>(requestUrl, { selectedSalaires: entity.selectedSalaires });
    return response;
  },
  { serializeError: serializeAxiosError }
);

export const relanceSalairePayment = createAsyncThunk(
  'salaire/relance_pay_salaire',
  async (entity: ISalaireRelanceDTO, thunkAPI) => {
    const response = await axios.post<ISalaire[]>(
      `${apiUrl}/relance`,
      {},
      {
        params: {
          salaireId: entity.salaireId,
          moyenPaiement: entity.moyenPaiement,
          numeroPaiement: entity.numeroPaiement,
        },
      }
    );
    thunkAPI.dispatch(getSalaireById(entity.salaireId));
    return response;
  },
  { serializeError: serializeAxiosError }
);

export const initializeSalaireGroup = createAsyncThunk(
  'salaire/initialize_salaire_group',
  async (entity: ISalaireGroupDTO, thunkAPI) => {
    const result = await axios.post<ISalaire[]>(
      `${apiUrl}/group/initialize`,
      {},
      {
        params: {
          ecoleId: entity.ecoleId,
          mois: entity.mois,
          anneeScolaireId: entity.anneeScolaireId,
        },
      }
    );
    thunkAPI.dispatch(getSalaireGroupsByEcoleId({ ecoleId: entity.ecoleId, anneeScolaireId: entity.anneeScolaireId }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteSalaireGroup = createAsyncThunk(
  'salaire/delete_salaire_group',
  async ({ salaireGroupId, ecoleId, anneeScolaireId }: { salaireGroupId: string; ecoleId: string; anneeScolaireId: string }, thunkAPI) => {
    const requestUrl = `${apiUrl}/group/${salaireGroupId}`;
    await axios.delete(requestUrl);
    thunkAPI.dispatch(getSalaireGroupsByEcoleId({ ecoleId, anneeScolaireId }));
    return salaireGroupId;
  },
  { serializeError: serializeAxiosError }
);

export const getSalaireById = createAsyncThunk(
  'salaire/fetch_salaire',
  async (salaireId: string) => {
    const requestUrl = `api/salaires/details/${salaireId}`;
    const response = await axios.get<SalaireDetails>(requestUrl);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateSalaire = createAsyncThunk(
  'facture/update-salaire',
  async (entity: SalaireDetails, thunkAPI) => {
    if (entity.id === undefined) {
      throw new Error('ID is undefined');
    }
    const result = await axios.put<SalaireDetails>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getSalaireById(entity.id));
    return result.data;
  },
  { serializeError: serializeAxiosError }
);

export const getDashboardSalaires = createAsyncThunk('factures/fetch_salaire_dashboard', async (salaireGroupId: string) => {
  const requestUrl = `${apiUrl}/group/${salaireGroupId}/list?all=${true}`;
  const response = await axios.get<SalaireDetails[]>(requestUrl);
  const totalItems = response.headers['x-total-count'];

  return { salaires: response.data, totalItems };
});

export const getValidSalairesForPayment = createAsyncThunk(
  'salaire/fetch_valid_salaires',
  async (salaireGroupId: string) => {
    const requestUrl = `${apiUrl}/group/${salaireGroupId}/valid-salaires`;
    const response = await axios.get<string[]>(requestUrl);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const calculateTotalAmount = createAsyncThunk(
  'salaire/calculate_total_amount',
  async ({ salaireGroupId, selectedSalaires }: { salaireGroupId: string; selectedSalaires: string[] }) => {
    const requestUrl = `${apiUrl}/group/${salaireGroupId}/calculate-total`;
    const response = await axios.post<number>(requestUrl, { selectedSalaires });
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

// Slice
export type SalaireState = Readonly<typeof initialState>;

const salaireSlice = createSlice({
  name: 'salaire',
  initialState: initialState as SalaireState,
  reducers: {
    reset() {
      return initialState;
    },
    setSelectedSalaires(state, action: PayloadAction<string[]>) {
      state.selectedSalaires = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addMatcher(isFulfilled(getSalaireGroupsByEcoleId), (state, action) => {
        state.loading = false;
        state.salaireGroupEntities = action.payload.data;
      })
      .addMatcher(isFulfilled(getSalaireGroupById), (state, action) => {
        state.loading = false;
        state.salaireGroup = action.payload.data;
      })
      .addMatcher(isFulfilled(getSalaireDashboard), (state, action) => {
        state.loading = false;
      })
      .addMatcher(isFulfilled(initializeSalaireGroup), (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.entities = action.payload.data;
      })
      .addMatcher(isFulfilled(partialUpdateSalaire), (state, action) => {
        state.loading = false;
        state.entity = action.payload;
      })
      .addMatcher(isFulfilled(paySalairesToProfesseurs), (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.entities = action.payload.data;
      })
      .addMatcher(isFulfilled(relanceSalairePayment), (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.entities = action.payload.data;
      })
      .addMatcher(isFulfilled(getDashboardSalaires), (state, action) => {
        state.loading = false;
        state.dashBoardEntities = action.payload.salaires;
        state.totalItems = action.payload.totalItems;
      })
      .addMatcher(isFulfilled(fetchSalaires), (state, action) => {
        state.loading = false;
        state.entities = action.payload.salaireWithDetails;
        state.totalItems = action.payload.totalItems;
      })
      .addMatcher(isFulfilled(getSalairesByProfesseurId), (state, action) => {
        state.loading = false;
        state.entities = action.payload.salaireWithDetails;
      })
      .addMatcher(isFulfilled(deleteSalaireGroup), (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.salaireGroupEntities = state.salaireGroupEntities.filter(entity => entity.id !== action.payload);
      })
      .addMatcher(isFulfilled(getSalaireById), (state, action) => {
        state.loading = false;
        state.entity = action.payload;
      })
      .addMatcher(isFulfilled(getValidSalairesForPayment), (state, action) => {
        state.loading = false;
        state.selectedSalaires = action.payload;
      })
      .addMatcher(isFulfilled(calculateTotalAmount), (state, action) => {
        state.loading = false;
        state.totalAmount = action.payload;
      })
      .addMatcher(
        isPending(
          getSalaireGroupsByEcoleId,
          getSalaireGroupById,
          getSalaireById,
          fetchSalaires,
          getSalaireDashboard,
          getSalairesByProfesseurId,
          getValidSalairesForPayment,
          calculateTotalAmount
        ),
        state => {
          state.loading = true;
          state.errorMessage = null;
          state.updateSuccess = false;
        }
      )
      .addMatcher(
        isPending(
          initializeSalaireGroup,
          paySalairesToProfesseurs,
          relanceSalairePayment,
          deleteSalaireGroup,
          getValidSalairesForPayment,
          calculateTotalAmount
        ),
        state => {
          state.updating = true;
          state.errorMessage = null;
          state.updateSuccess = false;
        }
      )
      .addMatcher(
        isRejected(
          getSalaireGroupsByEcoleId,
          getSalaireGroupById,
          getSalaireDashboard,
          initializeSalaireGroup,
          paySalairesToProfesseurs,
          relanceSalairePayment,
          deleteSalaireGroup,
          getSalaireById,
          fetchSalaires,
          getSalairesByProfesseurId,
          partialUpdateSalaire,
          getDashboardSalaires,
          getValidSalairesForPayment,
          calculateTotalAmount
        ),
        (state, action) => {
          state.loading = false;
          state.updating = false;
          state.errorMessage = action.error.message || null;
        }
      );
  },
});

export const { reset, setSelectedSalaires } = salaireSlice.actions;

export default salaireSlice.reducer;
